import React, { useCallback, useRef, useState } from 'react';
import { AlertCircle, Loader2, ShieldCheck } from 'lucide-react';
import { EligibilityService } from '../../services/EligibilityService';
import { EligibilityCheckResult, VerifyEligibilityInput } from '../../types/eligibility';

/**
 * Triggers an eligibility check and reports the result upward.
 *
 * Two behaviors worth knowing about:
 *
 * 1. Concurrent clicks are ignored while a check is in flight. Each real check
 *    is billable, and double-submitting is the easiest way to pay twice for the
 *    same answer.
 * 2. Missing patient data is reported before the call, not after. Sending an
 *    incomplete 270 costs the same as a complete one and always fails.
 */

interface VerifyCoverageButtonProps {
  input: VerifyEligibilityInput | null;
  onResult: (result: EligibilityCheckResult) => void;
  onError?: (message: string) => void;
  /** Skip the cache and force a fresh, billable check. */
  forceRefresh?: boolean;
  label?: string;
  disabled?: boolean;
  variant?: 'primary' | 'secondary';
}

const COLORS = {
  accent: '#6CA6C1',
  accentDeep: '#28527A',
  border: '#374151',
  text: '#f9fafb',
  danger: '#ef4444',
};

/** Fields the payer will reject the request without. */
const REQUIRED_FIELDS: Array<{ key: keyof VerifyEligibilityInput; label: string }> = [
  { key: 'payerId', label: 'insurance payer' },
  { key: 'memberId', label: 'member ID' },
  { key: 'firstName', label: 'first name' },
  { key: 'lastName', label: 'last name' },
  { key: 'dateOfBirth', label: 'date of birth' },
  { key: 'providerNpi', label: 'provider NPI' },
];

export function findMissingFields(input: VerifyEligibilityInput | null): string[] {
  if (!input) return ['insurance details'];
  return REQUIRED_FIELDS.filter(({ key }) => !input[key]).map(({ label }) => label);
}

const VerifyCoverageButton: React.FC<VerifyCoverageButtonProps> = ({
  input,
  onResult,
  onError,
  forceRefresh = false,
  label = 'Verify Coverage',
  disabled = false,
  variant = 'primary',
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inFlight = useRef(false);

  const missing = findMissingFields(input);
  const blocked = disabled || missing.length > 0;

  const verify = useCallback(async () => {
    // Guard with a ref, not state: two clicks in the same tick both see the
    // stale state value, and each one costs money.
    if (inFlight.current || !input) return;

    inFlight.current = true;
    setLoading(true);
    setError(null);

    try {
      onResult(await EligibilityService.verify({ ...input, forceRefresh }));
    } catch (caught) {
      const message =
        caught instanceof Error ? caught.message : 'Coverage check failed. Please try again.';
      setError(message);
      onError?.(message);
    } finally {
      inFlight.current = false;
      setLoading(false);
    }
  }, [input, forceRefresh, onResult, onError]);

  const primary = variant === 'primary';

  return (
    <div>
      <button
        type="button"
        onClick={verify}
        disabled={blocked || loading}
        aria-busy={loading}
        aria-describedby={missing.length ? 'verify-coverage-missing' : undefined}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          padding: '10px 16px',
          borderRadius: '0.5rem',
          border: primary ? 'none' : `1px solid ${COLORS.border}`,
          background: primary ? COLORS.accentDeep : 'transparent',
          color: primary ? COLORS.text : COLORS.accent,
          fontSize: '14px',
          fontWeight: 500,
          cursor: blocked || loading ? 'not-allowed' : 'pointer',
          opacity: blocked || loading ? 0.6 : 1,
        }}
      >
        {loading ? (
          <Loader2 size={16} aria-hidden="true" />
        ) : (
          <ShieldCheck size={16} aria-hidden="true" />
        )}
        {loading ? 'Checking with payer…' : label}
      </button>

      {/* Say what is missing rather than presenting a dead button. */}
      {missing.length > 0 && (
        <p
          id="verify-coverage-missing"
          style={{ margin: '6px 0 0', fontSize: '12px', color: '#9ca3af' }}
        >
          Add {missing.join(', ')} to verify coverage.
        </p>
      )}

      {error && (
        <p
          role="alert"
          style={{
            margin: '8px 0 0',
            fontSize: '13px',
            color: COLORS.danger,
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          <AlertCircle size={14} aria-hidden="true" />
          {error}
        </p>
      )}
    </div>
  );
};

export default VerifyCoverageButton;
